import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

/**
 * ConfirmDialog — substitui os window.confirm() nativos por um modal próprio.
 * Como o confirm() nativo é SÍNCRONO e o modal é assíncrono, useConfirm()
 * devolve uma Promise<boolean>:
 *
 *   const confirmar = useConfirm();
 *   const ok = await confirmar({
 *     titulo: 'Cancelar NF?',
 *     mensagem: 'Esta ação é irreversível.',
 *     confirmar: 'Cancelar NF',
 *     perigo: true,
 *   });
 *   if (!ok) return;
 *
 * Acessível: role="dialog" aria-modal, Esc cancela, foco inicial no botão
 * cancelar (default seguro), clique no overlay cancela. Montado via
 * <ConfirmProvider> na raiz. Aditivo.
 */

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [estado, setEstado] = useState(null);
  const resolver = useRef(null);
  const cancelBtn = useRef(null);

  const confirmar = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolver.current = resolve;
      setEstado({
        titulo: opts.titulo || 'Confirmar',
        mensagem: opts.mensagem || opts.message || 'Tem certeza?',
        textoConfirmar: opts.confirmar || 'Confirmar',
        textoCancelar: opts.cancelar || 'Cancelar',
        perigo: !!opts.perigo,
      });
    });
  }, []);

  const fechar = useCallback((valor) => {
    if (resolver.current) {
      resolver.current(valor);
      resolver.current = null;
    }
    setEstado(null);
  }, []);

  // Esc cancela, Enter confirma. Foco inicial no cancelar.
  useEffect(() => {
    if (!estado) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); fechar(false); }
      else if (e.key === 'Enter') { e.preventDefault(); fechar(true); }
    };
    document.addEventListener('keydown', onKey);
    const t = setTimeout(() => { if (cancelBtn.current) cancelBtn.current.focus(); }, 0);
    return () => { document.removeEventListener('keydown', onKey); clearTimeout(t); };
  }, [estado, fechar]);

  return (
    <ConfirmContext.Provider value={confirmar}>
      {children}
      {estado && typeof document !== 'undefined' && ReactDOM.createPortal(
        <div
          className="ui-modal-overlay"
          onMouseDown={(e) => { if (e.target === e.currentTarget) fechar(false); }}
        >
          <div className="ui-modal" role="dialog" aria-modal="true" aria-labelledby="ui-confirm-title">
            <h3 id="ui-confirm-title" className="ui-modal__title">{estado.titulo}</h3>
            <div className="ui-modal__body">{estado.mensagem}</div>
            <div className="ui-modal__actions">
              <button
                ref={cancelBtn}
                type="button"
                className="ui-btn ui-btn--ghost"
                onClick={() => fechar(false)}
              >{estado.textoCancelar}</button>
              <button
                type="button"
                className={`ui-btn ${estado.perigo ? 'ui-btn--danger' : 'ui-btn--primary'}`}
                onClick={() => fechar(true)}
              >{estado.textoConfirmar}</button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm precisa estar dentro de <ConfirmProvider>');
  return ctx;
}

export default ConfirmProvider;
